import React, { useState, useEffect } from 'react';
import { Check, X, Loader, ChevronLeft, ChevronRight, Download, Info, ChevronDown, ChevronUp, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  getActivityLogs,
  exportLogs,
  LogEntry
} from '../services/api/activityLogsService';
import { getClientOrg } from '../components/common/utilities/fetchWrap';

interface Pagination {
  total: number;
  current: number;
  perPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface Filters {
  startTime: string;
  endTime: string;
}


const ActivityLogs: React.FC = () => {
  // Initialize with today's date range
  const getInitialDates = () => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    
    return {
      startTime: startOfDay.toISOString().slice(0, 16),
      endTime: endOfDay.toISOString().slice(0, 16)
    };
  };

  const initialFilters: Filters = getInitialDates();

  // Get min and max date constraints
  const getDateConstraints = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return {
      min: thirtyDaysAgo.toISOString().slice(0, 16),
      max: now.toISOString().slice(0, 16)
    };
  };

  const initialPagination: Pagination = {
    total: 0,
    current: 1,
    perPage: 15,
    hasNextPage: false,
    hasPreviousPage: false
  };

  // Component state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(true);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  // Define fetchLogs function first - memoize it to prevent unnecessary renders
  const fetchLogs = React.useCallback(async () => {
    setLoadingLogs(true);
    try {
      const result = await getActivityLogs({
        startTime: appliedFilters.startTime,
        endTime: appliedFilters.endTime,
        page: pagination.current,
        perPage: pagination.perPage
      });
      
      setLogs(result.logs);
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total,
        hasNextPage: result.pagination.hasNextPage,
        hasPreviousPage: result.pagination.hasPreviousPage
      }));
      // Only update timestamp if we have results
      if (result.logs.length > 0) {
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      if (error instanceof Error) {
        toast.error(`Failed to load activity logs: ${error.message}`);
      } else {
        toast.error('Unable to load activity logs. Please try again later.');
      }
    } finally {
      setLoadingLogs(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters.startTime, appliedFilters.endTime, pagination.current, pagination.perPage, refreshCounter]);

  // Set initial load flag on mount
  useEffect(() => {
    setHasInitialLoad(true);
  }, []);



  const handleApplyFilters = () => {
    // Validate date range
    const start = new Date(filters.startTime);
    const end = new Date(filters.endTime);
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Check if start date is more than 30 days ago
    if (start < thirtyDaysAgo) {
      toast.error('Start date cannot be more than 30 days in the past');
      return;
    }

    // Check if end date is more than 30 days ago
    if (end < thirtyDaysAgo) {
      toast.error('End date cannot be more than 30 days in the past');
      return;
    }

    // Check if dates are in the future
    if (start > now) {
      toast.error('Start date cannot be in the future');
      return;
    }

    if (end > now) {
      toast.error('End date cannot be in the future');
      return;
    }

    // Check if end date is after start date
    if (end < start) {
      toast.error('End date must be after start date');
      return;
    }

    // Apply the current filters
    setAppliedFilters(filters);
    
    // Reset pagination to first page
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    // Increment refresh counter to force a new fetch
    setRefreshCounter(prev => prev + 1);
  };
  
  // Add effect to fetch logs when filters or pagination changes
  useEffect(() => {
    // Only fetch after initial action definitions load is complete
    if (hasInitialLoad) {
      fetchLogs();
    }
  }, [fetchLogs, hasInitialLoad]);

  const [exporting, setExporting] = useState<boolean>(false);
  
  // Function to handle log export
  const handleExportLogs = async () => {
    try {
      setExporting(true);
      
      const tenantId = getClientOrg();
      
      // Convert datetime-local format to ISO string for the API
      const startDate = appliedFilters.startTime ? new Date(appliedFilters.startTime).toISOString() : undefined;
      const endDate = appliedFilters.endTime ? new Date(appliedFilters.endTime).toISOString() : undefined;
      
      // Call the export API with date range
      const exportResponse = await exportLogs(tenantId, startDate, endDate);
      
      // Create a hidden link element to trigger the download
      const link = document.createElement('a');
      link.href = exportResponse.downloadUrl;
      link.download = exportResponse.filename;
      link.target = '_blank';
      
      // Append to the DOM, click it to start download, then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      toast.success(`Successfully exported ${exportResponse.recordCount} logs to ${exportResponse.filename}`);
    } catch (error) {
      console.error('Error exporting logs:', error);
      // Show user-friendly error message
      if (error instanceof Error && error.message.includes('Network')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Unable to export logs. Please try again or contact support if the issue persists.');
      }
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      current: page
    }));
    // fetchLogs will be triggered by the useEffect hook
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SUCCESS':
        return 'bg-green-100 text-green-800';
      case 'ERROR':
        return 'bg-red-100 text-red-800';
      case 'INFO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SUCCESS':
        return <Check size={16} className="text-green-600" />;
      case 'ERROR':
        return <X size={16} className="text-red-600" />;
      case 'INFO':
        return <Info size={16} className="text-blue-600" />;
      default:
        return null;
    }
  };

  const formatDetails = (details: any, truncate: boolean = false) => {
    if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
      return '-';
    }

    // Convert camelCase/PascalCase keys to readable format
    const formatKey = (key: string) => {
      return key
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
        .trim();
    };

    // Create readable key-value pairs
    const formattedEntries = Object.entries(details)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => {
        const readableKey = formatKey(key);
        let readableValue = value;
        
        // Handle different value types
        if (typeof value === 'boolean') {
          readableValue = value ? 'Yes' : 'No';
        } else if (Array.isArray(value)) {
          readableValue = value.join(', ');
        } else if (typeof value === 'object') {
          readableValue = JSON.stringify(value);
        }
        
        return `${readableKey}: ${readableValue}`;
      });

    if (formattedEntries.length === 0) return '-';

    const fullText = formattedEntries.join('\n');
    
    if (truncate && fullText.length > 100) {
      return fullText.substring(0, 100) + '...';
    }
    
    return fullText;
  };

  const toggleDetailsExpansion = (logId: string) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedDetails(newExpanded);
  };

  const hasMoreDetails = (details: any) => {
    const formatted = formatDetails(details, false);
    return formatted !== '-' && formatted.length > 100;
  };

  const renderPagination = () => {
    const { total, current, hasNextPage, hasPreviousPage } = pagination;
    
    // Show pagination if there are results and either next or previous page exists
    if (total === 0 && !hasNextPage && !hasPreviousPage) {
      return null;
    }
    
    
    return (
      <div className="flex items-center justify-center border-t border-gray-200 px-4 py-3">
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={() => handlePageChange(current - 1)}
            disabled={!hasPreviousPage}
            className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
              !hasPreviousPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>
          
          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            Page {current}
          </span>
          
          <button
            onClick={() => handlePageChange(current + 1)}
            disabled={!hasNextPage}
            className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
              !hasNextPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </nav>
      </div>
    );
  };


  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-md font-medium mb-4">Log Filters</h3>
          <div className="flex items-end gap-4">
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={filters.startTime}
                onChange={(e) => setFilters({ ...filters, startTime: e.target.value })}
                min={getDateConstraints().min}
                max={getDateConstraints().max}
                className="w-56 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900 [color-scheme:light]"
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="datetime-local"
                value={filters.endTime}
                onChange={(e) => setFilters({ ...filters, endTime: e.target.value })}
                min={getDateConstraints().min}
                max={getDateConstraints().max}
                className="w-56 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900 [color-scheme:light]"
              />
            </div>
            <div className="flex-shrink-0">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleApplyFilters}
              >
                Search Logs
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden relative">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium">Activity Logs</h3>
            <div className="flex items-center gap-3">
              {logs.length > 0 && (
                <div className="flex items-center text-sm text-gray-500">
                  <Clock size={16} className="mr-1" />
                  Last updated: {lastUpdated.getFullYear()}-{String(lastUpdated.getMonth() + 1).padStart(2, '0')}-{String(lastUpdated.getDate()).padStart(2, '0')} {String(lastUpdated.getHours()).padStart(2, '0')}:{String(lastUpdated.getMinutes()).padStart(2, '0')}
                </div>
              )}
              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setRefreshCounter(prev => prev + 1)}
                disabled={loadingLogs}
              >
                <RefreshCw size={16} className={`mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleExportLogs}
                disabled={exporting || loadingLogs}
              >
                {exporting ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} className="mr-2" />
                    Export Logs
                  </>
                )}
              </button>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Time
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Status
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Action
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Category
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  User ID
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingLogs ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <Loader className="w-6 h-6 text-blue-500 animate-spin mr-2" />
                      <span className="text-gray-500">Loading logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-3 py-3 text-xs text-gray-500">
                      <div className="flex flex-col">
                        <span>{new Date(log.timestamp).toLocaleDateString()}</span>
                        <span className="text-gray-400">{new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(log.status)}`}>
                        {getStatusIcon(log.status)}
                        <span className="ml-1 hidden sm:inline">{log.status}</span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-gray-900">
                      <span className="truncate" title={log.action}>{log.action}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      <span className="truncate" title={log.category}>{log.category}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      <span >{log.user}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 max-w-xs">
                      <div className="flex items-start space-x-2">
                        <div className={expandedDetails.has(log.id) ? 'whitespace-pre-line' : 'truncate'}>
                          {expandedDetails.has(log.id) 
                            ? formatDetails(log.details, false)
                            : formatDetails(log.details, true)
                          }
                        </div>
                        {hasMoreDetails(log.details) && (
                          <button
                            onClick={() => toggleDetailsExpansion(log.id)}
                            className="flex-shrink-0 text-blue-600 hover:text-blue-800 focus:outline-none"
                            title={expandedDetails.has(log.id) ? 'Show less' : 'Show more'}
                          >
                            {expandedDetails.has(log.id) ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No logs found matching the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;